import React, { FC, useCallback, useMemo } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Button, Tag, message } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { CYCLE_EVENTS_CRUD_ACTION_CREATORS } from '../../actions/cycle_events_actions';
import { getStatusLabelFromPipelineFilters, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import CycleEventPipeLineFilters from './CycleEventPipelineFilters';
import {
	cycleEventsRestCrudThunksForBuyer,
	cycleEventsRestCrudThunksForSupplier,
	refreshCycleEventCounts,
} from '../../thunks/cycle_events_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import GeneralStatusDisplay from './GeneralStatusDisplay';
import { ASSET_FIELDS_LABELS_MAP } from '../../utils/assetUtils';
import { withUserType } from '../withUserType';
import ExcludedAssetTypesButton from '../excluded_asset_types_card/ExcludedAssetTypesCard';
import { ARCHIVED_STATUS_DEFAULT_FILTER, getArchivedStatusFilter } from '../../utils/FilterUtils';
import ArchiveRowAction from '../archive_row_action/ArchiveRowAction';

const ASSET_CYCLE_RUNS_TARGET_COLLECTION = 'AssetCycleRunsIndex';

const AdminCycleEventsIndexPage: FC<any> = ({
	history,
	location,
	updateFilters,
	clearAndUpdateFilters,
	refreshCounts,
	cycleEvents,
	userType,
	archiveCycleEvent,
	unarchiveCycleEvent,
	fetchCycleEvents,
}): React.ReactElement => {
	// Default to Active only when the URL doesn't already specify a status, so refreshing
	// or opening an Archived/All deep link keeps its `isArchived` query param.
	const initialArchivedFilters = useMemo(
		() =>
			new URLSearchParams(location.search).has('isArchived') ? {} : ARCHIVED_STATUS_DEFAULT_FILTER,
		[location.search]
	);

	const getStateSliceProp = useCallback(
		(propName) =>
			nullSafeGetOrElse(`${ASSET_CYCLE_RUNS_TARGET_COLLECTION}.${propName}`, cycleEvents, {}),
		[cycleEvents]
	);

	const refreshCycleEvents = useCallback(() => {
		refreshCounts(getStateSliceProp('filters'));
		return fetchCycleEvents(
			{},
			ASSET_CYCLE_RUNS_TARGET_COLLECTION,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
	}, [fetchCycleEvents, getStateSliceProp, refreshCounts]);

	const handleArchiveCycleEvent = useCallback(
		(cycleEvent) =>
			archiveCycleEvent(cycleEvent).then(() => {
				message.success('Asset cycle event archived.');
				return refreshCycleEvents();
			}),
		[archiveCycleEvent, refreshCycleEvents]
	);

	const handleUnarchiveCycleEvent = useCallback(
		(cycleEvent) =>
			unarchiveCycleEvent(cycleEvent).then(() => {
				message.success('Asset cycle event restored.');
				return refreshCycleEvents();
			}),
		[unarchiveCycleEvent, refreshCycleEvents]
	);

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			defaultSortOrder: 'ascend',
			sorter: true,
		},
		{
			title: 'Assets',
			dataIndex: 'assetTypeCount',
			defaultSortOrder: 'ascend',
			sorter: true,
			render: (text, record) => {
				return (
					<div className="bodyregular flex flex-row items-center gap-2 text-grey-8">
						{`${nullSafeGetOrElse('totalAssetCount', record, 0)} • ${nullSafeGetOrElse(
							'assetTypeCount',
							record,
							0
						)} Asset Groups`}
					</div>
				);
			},
		},
		{
			title: 'Locations',
			dataIndex: 'locationCount',
			defaultSortOrder: 'ascend',
			sorter: true,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			defaultSortOrder: 'ascend',
			render: (status) => (
				<GeneralStatusDisplay
					statusKey={status}
					statusLabel={getStatusLabelFromPipelineFilters(CycleEventPipeLineFilters, status)}
				/>
			),
			sorter: true,
		},
		{
			title: 'Fields Captured',
			dataIndex: 'captureFields',
			defaultSortOrder: 'ascend',
			render: (text, record) => {
				return nullSafeGetOrElse('captureFields.length', record, 0) > 0 ? (
					<div className="flex flex-wrap gap-2">
						{(record.captureFields || []).map((field) => (
							<Tag key={field.key} style={{ margin: 0 }}>
								<span className="mr-1 inline-block">
									{field.isRequired ? <span className="text-red-500">*</span> : null}
								</span>
								{ASSET_FIELDS_LABELS_MAP[field.key] || field.key}
							</Tag>
						))}
					</div>
				) : (
					'--'
				);
			},
		},
		{
			title: '',
			dataIndex: 'id',
			render: (text, record) => (
				<div className="flex flex-row items-center justify-end">
					<ArchiveRowAction
						entity={record}
						onArchive={handleArchiveCycleEvent}
						onUnarchive={handleUnarchiveCycleEvent}
					/>
				</div>
			),
		},
	];
	const onRow = (record) => ({
		onClick: () => {
			let searchString = location.search;
			if (searchString[0] === '?') {
				searchString = searchString.slice(1);
			}
			// Carry the originating overview path (which holds the Active/Archived/All status
			// filter in its query) so returning from details lands on the same view rather than
			// always the default. `backlinkParams` preserves the rest of the query state.
			const params = [`backTab=${encodeURIComponent(location.pathname)}`];
			if (searchString && searchString.length > 0) {
				params.push(`backlinkParams=${encodeURIComponent(searchString)}`);
			}
			history.push(`/${userType}/assetcycleevents/detail/${record.id}/details?${params.join('&')}`);
		},
	});

	return (
		<PaginatedReduxTableWithHeader
			targetCollectionName={ASSET_CYCLE_RUNS_TARGET_COLLECTION}
			stateSlice={cycleEvents}
			entityCollectionName="cycle_events"
			updateFilters={updateFilters}
			clearAndUpdateFilters={clearAndUpdateFilters}
			handleQueryParams
			refreshCounts={refreshCounts}
			pipeLineFilters={CycleEventPipeLineFilters}
			filterConfig={[getArchivedStatusFilter()]}
			emptyState={
				<EmptyState
					graphic={
						<img
							alt="Currently there are no events!"
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
						/>
					}
					headline={"It's a little lonely in here."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}>
								Looks like your team has not added any cycle runs yet.
							</div>

							<Button
								type="primary"
								onClick={() => history.push(`/${userType}/assetcycleevents/new`)}
							>
								Add your first event
							</Button>
						</div>
					}
				/>
			}
			rightActions={
				<div className="flex flex-row items-center gap-2">
					<ExcludedAssetTypesButton />
					<Button
						type="primary"
						size="large"
						onClick={() => history.push(`/${userType}/assetcycleevents/new`)}
					>
						Create Event
					</Button>
				</div>
			}
			onTableRow={onRow}
			showHeader
			tableColumns={columns}
			initialFilters={initialArchivedFilters}
			initialSorting={{ sort_by: 'createdAt', order: 'desc' }}
			fetchData={
				userType === ROLE_TYPES.SUPPLIER
					? cycleEventsRestCrudThunksForSupplier.read
					: cycleEventsRestCrudThunksForBuyer.read
			}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	cycleEvents: state.cycle_events,
	companySettings: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(CYCLE_EVENTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			CYCLE_EVENTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	refreshCounts: (filters) => dispatch(refreshCycleEventCounts(ownProps.userType)(filters)),
	fetchCycleEvents: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier
				: cycleEventsRestCrudThunksForBuyer
			).read(params, targetCollectionName, pagination, sorting, filters)
		),
	archiveCycleEvent: (cycleEvent) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier
				: cycleEventsRestCrudThunksForBuyer
			).archive(cycleEvent)
		),
	unarchiveCycleEvent: (cycleEvent) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier
				: cycleEventsRestCrudThunksForBuyer
			).unarchive(cycleEvent)
		),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(AdminCycleEventsIndexPage))
);
